import { createContext, useCallback, useContext, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useCurrentSidebarFieldNodeId, useCurrentSidebarId } from './useCurrentTab.tsx';
import type { JiraIssueFieldConfig } from './JiraFieldCategoryType.types.tsx';
import type { AddSchemesSidebarContextValueType } from './FormData.types.tsx';
import type { ColumnsSidebarContextValueType } from './ColumnsSidebarContextValueType.types.tsx';
import type { CreateSidebarContextValueType } from './FormDataCreate.types.tsx';
import type { EditSidebarContextValueType } from './FormDataEdit.types.tsx';
import type { ProjectsSidebarContextValueType } from './ProjectsSidebarRuntimeProps.types.tsx';
import type { SchemesSidebarContextValueType } from './SchemesSidebarContextValueType.types.tsx';
import type { ScreensSidebarContextValueType } from './ScreensSidebarContextValueType.types.tsx';

// Export for convenience
export type SidebarTriggerOnClick = (
	e?: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
	analyticsEvent?: UIAnalyticsEvent,
) => void;

export const SidebarsContext = createContext<TSidebarsContext | null>(null);

const useSidebarsContext = () => {
	const context = useContext(SidebarsContext);
	if (!context) {
		throw new Error('useSidebarsContext is not defined within a SidebarsContextProvider');
	}
	return context;
};

export const useSidebars = <T extends keyof TSidebarsContext>(
	id: T,
	fieldNodeId?: JiraIssueFieldConfig['id'],
) => {
	const sidebarContext = useSidebarsContext();
	const { setCurrentSidebarId, currentSidebarId } = useCurrentSidebarId();
	const { setCurrentSidebarFieldNodeId, currentSidebarFieldNodeId } =
		useCurrentSidebarFieldNodeId();
	const sidebarContextValue = sidebarContext[id];

	if (!('SidebarTrigger' in sidebarContextValue)) {
		throw new Error(`useOpenSidebar for ${id} must contain a SidebarTrigger`);
	}

	if (!('openSidebar' in sidebarContextValue)) {
		throw new Error(`useOpenSidebar for ${id} must contain a function openSidebar`);
	}

	const openSidebar: TSidebarsContext[T]['openSidebar'] = useCallback(
		// @ts-expect-error - This is type safe
		(...args) => {
			setCurrentSidebarId(id);
			// We want to clear even if the fieldNodeId is not provided because,
			// We don't want to flash the new sidebar with the old fieldNodeId
			setCurrentSidebarFieldNodeId(fieldNodeId);
			// @ts-expect-error - This is type safe
			sidebarContextValue.openSidebar(...args);
		},

		[sidebarContextValue, setCurrentSidebarId, setCurrentSidebarFieldNodeId, id, fieldNodeId],
	);

	return useMemo(() => {
		return {
			...sidebarContextValue,
			openSidebar,
			currentSidebarId,
			currentSidebarFieldNodeId,
		};
	}, [sidebarContextValue, openSidebar, currentSidebarId, currentSidebarFieldNodeId]);
};

type TSidebarsContext = Readonly<{
	ADDSCHEMES: AddSchemesSidebarContextValueType;
	CREATE: CreateSidebarContextValueType;
	COLUMNS: ColumnsSidebarContextValueType;
	EDIT: EditSidebarContextValueType;
	PROJECTS: ProjectsSidebarContextValueType;
	SCHEMES: SchemesSidebarContextValueType;
	SCREENS: ScreensSidebarContextValueType;
}>;
